import { useCallback, useEffect, useMemo, useState } from 'react';

import { skipToken } from '@reduxjs/toolkit/query';
import { selectMyCommunityId } from 'community/api';
import { useAppSelector } from 'store';
import { asActionSet, useGetMyPermissionsQuery } from 'store/apis/permissionsApi';

import { CATALOGUE_ACTIONS, scopeKindOf, scopeTag } from '../../core';
import { reportUnanswerablePermissions } from '../../telemetry';
import { resolveServiceScope } from './resolveServiceScope';
import { toServiceScope } from './toServiceScope';

import type { Action, PermissionsScope, PermissionsSnapshot, PermissionsSource } from '../../core';
import type { CommunityId } from 'domain/CommunityId';
import type { PermissionsQueryError } from 'store/apis/permissionsApi';

/**
 * The service contributes exactly the published catalogue - every action, whether or not it grants it.
 *
 * Declaring the full catalogue rather than only what came back is what makes a denial determined: an
 * action the service could have granted and did not is a settled no, not a gate still waiting.
 */
export const SERVICE_CONTRIBUTES: ReadonlySet<Action> = new Set<Action>(CATALOGUE_ACTIONS);

/** Shared so an unanswered service source keeps one identity across renders. */
const NO_GRANTS: ReadonlySet<Action> = new Set<Action>();

/**
 * How long to wait before a second attempt, doubling per consecutive failure and capped.
 *
 * A degraded service otherwise gets a retry button pressed by every affected user at once, at the
 * moment it can least afford it - and every gate on the page offers the same control, so one user
 * can be several presses. Reset on success (R19).
 */
const RETRY_BACKOFF_STEP_MS = 2_000;
const RETRY_BACKOFF_CAP_MS = 30_000;

const backoffFor = (consecutiveFailures: number) =>
  Math.min(RETRY_BACKOFF_STEP_MS * 2 ** Math.max(0, consecutiveFailures - 1), RETRY_BACKOFF_CAP_MS);

/**
 * Per scope, and module-scoped rather than per hook.
 *
 * This hook runs once per `useCan` call - once per sidebar link - so per-hook state would give each
 * gate its own private backoff, and one user pressing "try again" on the sidebar would not be
 * spaced against the same user pressing it on the page behind it.
 */
const failuresByScope = new Map<string, number>();
const lastAttemptByScope = new Map<string, number>();

/**
 * The failure each scope's count has already been charged for.
 *
 * Incrementing a counter is not idempotent, and this hook runs once per mounted gate, so without
 * this one outage would count once per gate - seven on the sidebar alone - and a single failure
 * would land straight on the thirty-second cap. Every gate on a scope reads one shared RTK Query
 * cache entry, so the error's identity is what "the same failure" means here: a later request
 * fails with a new object and is charged again.
 */
const countedErrorByScope = new Map<string, unknown>();

/** Test-only: the backoff is module state by design, so a suite has to be able to clear it. */
export const resetPermissionsRetryState = () => {
  failuresByScope.clear();
  lastAttemptByScope.clear();
  countedErrorByScope.clear();
};

/**
 * The port, answered by `permissions-service`, and only by it: every action in the vocabulary is
 * one the service serves.
 *
 * Only `usePermissionsSnapshot` may name this hook. Reaching it from a call site would defeat the
 * one-file adapter choice the whole layer is built around.
 *
 * @param scope which community or organisation the answer is about. Defaults to `selectMyCommunityId`
 * - the *filtered* selector, so this never asks the service about a community the user has not
 * joined (KTD7). With no scope resolved no request is sent, and catalogue actions settle as denials
 * rather than spinners (AE6).
 */
export const useServicePermissionsSnapshot = (scope?: PermissionsScope): PermissionsSnapshot => {
  const myCommunityId = useAppSelector(selectMyCommunityId);

  // Held as primitives rather than as a scope object: rebuilding the object each render would make
  // it an unstable memo dependency and hand every consumer a fresh `allowed` Set.
  const scopedOrganisationId = scope?.organisationId;
  const scopedCommunityId = scopedOrganisationId === undefined ? (scope?.communityId ?? myCommunityId) : undefined;

  const asked = resolveServiceScope(scopedCommunityId, scopedOrganisationId);

  /**
   * `skipToken` rather than `{ skip: true }`, because the argument itself is absent when no scope
   * resolves. A placeholder argument beside `skip` would land in the cache key and fire a request
   * for that placeholder the moment the flag flipped.
   */
  const {
    currentData,
    error,
    isFetching,
    isError: isQueryError,
    refetch,
  } = useGetMyPermissionsQuery(asked ? toServiceScope(asked) : skipToken);

  const tag = scopeTag(asked);

  // A primitive, so the failure effect below can depend on it rather than on `asked` - which is
  // rebuilt every render and would re-run the effect on each one.
  const scopeKind = asked === undefined ? 'community' : scopeKindOf(asked);

  const isAsked = asked !== undefined;

  /**
   * Unanswered until the scope actually asked about either has an answer or has reported an error.
   *
   * Stated as the absence of an answer rather than as a set of RTK Query flags, because no flag
   * covers every unanswered state and the ones that look like they do are traps. `isLoading` is
   * first-load only, so a second attempt after a failure reports it false; `isUninitialized` is
   * long past by then; and `isFetching` is also true during a background refetch that already has
   * a good answer to serve, so holding on it would flicker-deny a settled gate.
   *
   * Reading any of those as settled hands `evaluate` a determined "no grants" the service never
   * gave. Observed live: a blocked service produced an `unexpected` shadow comparison in 48ms,
   * blaming the service for withholding what it was never reached to be asked about.
   */
  const isServiceLoading = isAsked && currentData === undefined && !isQueryError;

  /**
   * Strict, per KTD3: an error denies even over a previously good answer.
   *
   * Harsher than the derivation this replaced, which let a stale answer stand through a failed
   * refetch. The plan carried that asymmetry as an open question to close before cutover; it closed
   * strict, because the alternative - serving an answer the service has since disowned - is the quiet
   * failure KTD3 exists to reject.
   */
  const isServiceError = isAsked && isQueryError;

  /**
   * Count the failures, and report the first one.
   *
   * In an effect rather than during render because both are side effects, and charged against the
   * error's identity because the count has to move exactly once per settled outcome however many
   * gates are mounted. Keying on the scope alone is not enough: every mounted gate runs this effect,
   * and each would increment the same counter for the one failure they are all reading.
   */
  useEffect(() => {
    if (tag === undefined) return;

    if (isServiceError) {
      /**
       * The counter moves; the clock does not. The interval spaces *repeat* attempts, so the first
       * press after a failure goes through immediately - a user who has just been told the check
       * failed should not find the control already dead. `lastAttemptByScope` is written by `retry`
       * alone, which is what makes the window a gap between presses rather than a cooldown on the
       * failure.
       */
      if (countedErrorByScope.has(tag) && countedErrorByScope.get(tag) === error) return;

      countedErrorByScope.set(tag, error);
      failuresByScope.set(tag, (failuresByScope.get(tag) ?? 0) + 1);

      const queryError = error as PermissionsQueryError | undefined;
      reportUnanswerablePermissions({
        scopeKind,
        status: queryError?.status ?? 0,
        failureClass: queryError?.data?.failureClass ?? 'network',
      });

      return;
    }

    // Reset on success, so a blip does not leave a user waiting thirty seconds for their next retry.
    // `currentData`, because the counter is keyed by scope: `data` still holds the last scope that
    // answered, so it would clear a failing scope's backoff on the strength of a different one.
    if (currentData !== undefined) {
      failuresByScope.delete(tag);
      lastAttemptByScope.delete(tag);
      countedErrorByScope.delete(tag);
    }
  }, [tag, isServiceError, currentData, error, scopeKind]);

  /**
   * Re-render when the backoff window closes, so a disabled control re-enables itself.
   *
   * Without this the hint is only as fresh as the last render, and a user who waits out the window
   * sees a permanently dead button - worse than no button.
   */
  const [, setBackoffTick] = useState(0);
  const failures = tag === undefined ? 0 : (failuresByScope.get(tag) ?? 0);
  const lastAttemptAt = tag === undefined ? undefined : lastAttemptByScope.get(tag);
  const waitUntil = lastAttemptAt === undefined ? 0 : lastAttemptAt + backoffFor(failures);
  const remainingMs = waitUntil - Date.now();

  useEffect(() => {
    if (remainingMs <= 0) return undefined;

    const timer = setTimeout(() => setBackoffTick((tick) => tick + 1), remainingMs);

    return () => clearTimeout(timer);
  }, [remainingMs]);

  /**
   * Whether pressing the control would do anything.
   *
   * Only offered on an unanswerable answer: a determined denial has nothing to re-ask, and a pending
   * one is already asking.
   */
  const canRetry = isServiceError && !isFetching && remainingMs <= 0;

  /**
   * The window is enforced here as well as reported through `canRetry`, and that is the load-bearing
   * half: `canRetry` is a render-time hint that can be stale, while this runs at the moment of the
   * click. A second press inside the interval issues no request whatever the button looks like.
   */
  const retry = useCallback(() => {
    if (tag === undefined || isFetching) return;

    const waitingUntil = (lastAttemptByScope.get(tag) ?? 0) + backoffFor(failuresByScope.get(tag) ?? 0);
    if (Date.now() < waitingUntil) return;

    lastAttemptByScope.set(tag, Date.now());
    void refetch();
  }, [tag, isFetching, refetch]);

  return useMemo(() => {
    const service: PermissionsSource = {
      // `currentData` rather than `data`: on an argument change RTK Query keeps serving the
      // previous argument's result until the new one lands, which here would fill a snapshot
      // labelled with one community from a different community's answer (R9).
      //
      // `asActionSet` is memoised on the cached array's identity, so this costs one lookup and an
      // unchanged answer keeps the same Set - which is what stops `evaluate` seeing a new snapshot
      // on every refetch.
      allowed: currentData ? asActionSet(currentData) : NO_GRANTS,
      contributes: SERVICE_CONTRIBUTES,
      isLoading: isServiceLoading,
      isError: isServiceError,
    };

    return {
      retry,
      canRetry,
      sources: [service],
      // The scope actually asked about, which is what stops one community's answer standing in for
      // another's (R9): `evaluate` compares a fact's community against this before letting the
      // snapshot grant an event action.
      scope: resolveServiceScope(scopedCommunityId, scopedOrganisationId),
    };
  }, [currentData, isServiceLoading, isServiceError, scopedCommunityId, scopedOrganisationId, retry, canRetry]);
};
